import React, { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { IconChevronDown, IconCalendar, IconClock } from "@tabler/icons-react";

import { MDialog } from "@/components/ui/atoms/MDialog";
import { MButton } from "@/components/ui/atoms/MButtons";
import MTextArea from "@/components/ui/atoms/MTextArea";
import { setNativeKeyboardAvoid } from "@/utils/nativeBridge";
import MTimePicker, {
  formatTimeString,
} from "@/components/ui/atoms/MTimePicker";
import MDatePicker from "@/components/ui/molecules/MDatePicker";
import MDropDownDrawer, {
  DropdownItem,
} from "@/components/ui/molecules/MDropDownDrawer";
import vitalsTrackingService from "@/services/vitals-tracking.service";
import {
  VitalsParameter,
  RecordReadingDto,
  VitalsReading,
  UpdateReadingDto,
} from "@/types/vitals";

import styles from "./AddReadingModal.module.css";

// ── Constants ─────────────────────────────────────────────────────────────────

const CONTEXT_OPTIONS = [
  { value: "AFTER_WALK", label: "After exercise" },
  { value: "BEFORE_SLEEP", label: "Before sleep" },
  { value: "MISSED_MEDICATION", label: "Missed medicine" },
  { value: "FEELING_UNWELL", label: "Feeling unwell" },
  { value: "AFTER_MEDICATION", label: "After medicine" },
  { value: "RESTING", label: "Resting" },
] as const;

const GLUCOSE_CATEGORIES = [
  "Fasting",
  "Post-prandial",
  "Random",
  "Before bed",
] as const;

type ContextValue = (typeof CONTEXT_OPTIONS)[number]["value"] | null;
type GlucoseCategory = (typeof GLUCOSE_CATEGORIES)[number];

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayDateString(): string {
  return format(new Date(), "yyyy-MM-dd");
}

function formatDisplayDate(dateStr: string): string {
  try {
    const [y, m, d] = dateStr.split("-").map(Number);
    return format(new Date(y, m - 1, d), "d MMM yyyy");
  } catch {
    return dateStr;
  }
}

function combineDateTime(dateStr: string, timeDate: Date): Date {
  const [y, m, d] = dateStr.split("-").map(Number);
  const result = new Date(y, m - 1, d);
  result.setHours(timeDate.getHours(), timeDate.getMinutes(), 0, 0);
  return result;
}

function lbsToKg(lbs: number): number {
  return Math.round(lbs * 0.4536 * 10) / 10;
}

// ── Shared sub-components ─────────────────────────────────────────────────────

interface WhenRowProps {
  date: string;
  timeDate: Date;
  onDateClick: () => void;
  onTimeClick: () => void;
}

const WhenRow: React.FC<WhenRowProps> = ({
  date,
  timeDate,
  onDateClick,
  onTimeClick,
}) => {
  const { t } = useTranslation();
  return (
    <div className={styles.fieldGroup}>
      <p className={styles.fieldLabel}>{t("vitalsTracker.addReading.when")}</p>
      <div className={styles.whenRow}>
        <button
          className={styles.dateField}
          onClick={onDateClick}
          type="button"
        >
          <span className={styles.dateDisplay}>{formatDisplayDate(date)}</span>
          <IconCalendar size={18} color="#383838" />
        </button>
        <button
          className={styles.timeField}
          onClick={onTimeClick}
          type="button"
        >
          <span className={styles.dateDisplay}>
            {formatTimeString(timeDate, 12)}
          </span>
          <IconClock size={18} color="#383838" />
        </button>
      </div>
    </div>
  );
};

interface NoteSectionProps {
  note: string;
  onChange: (v: string) => void;
  showLabel?: boolean;
  countBelow?: boolean;
}

const NOTE_MAX = 100;

const NoteSection: React.FC<NoteSectionProps> = ({
  note,
  onChange,
  showLabel = true,
  countBelow = false,
}) => {
  const { t } = useTranslation();

  // The modal disables native KeyboardAvoidingView globally (no shakiness for
  // top inputs), but the note textarea sits at the bottom and gets covered.
  // Re-enable KAV only while this textarea is focused so the native layer
  // pushes the view up, then restore the modal's default on blur.
  const handleFocus = () => setNativeKeyboardAvoid(true);
  const handleBlur = () => setNativeKeyboardAvoid(false);

  if (countBelow) {
    return (
      <div className={styles.fieldGroup}>
        {showLabel && (
          <p className={styles.fieldLabel}>
            {t("vitalsTracker.addReading.note")}
          </p>
        )}
        <div className={styles.noteTextareaWrap}>
          <MTextArea
            value={note}
            onChange={(e) => {
              if (e.target.value.length <= NOTE_MAX) onChange(e.target.value);
            }}
            placeholder={t("vitalsTracker.addReading.notePlaceholder")}
            rows={3}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>
        <span className={styles.noteCountBelow}>
          {NOTE_MAX - note.length} characters
        </span>
      </div>
    );
  }

  return (
    <div className={styles.noteTextareaWrap}>
      <MTextArea
        label={showLabel ? t("vitalsTracker.addReading.note") : undefined}
        labelClassName={styles.fieldLabel}
        value={note}
        onChange={(e) => onChange(e.target.value)}
        maxLength={NOTE_MAX}
        placeholder={t("vitalsTracker.addReading.notePlaceholder")}
        rows={3}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </div>
  );
};

// ── BP form ───────────────────────────────────────────────────────────────────

interface BPFormProps {
  systolic: string;
  diastolic: string;
  pulse: string;
  moreOpen: boolean;
  context: ContextValue;
  note: string;
  date: string;
  timeDate: Date;
  onSystolicChange: (v: string) => void;
  onDiastolicChange: (v: string) => void;
  onPulseChange: (v: string) => void;
  onMoreToggle: () => void;
  onContextDrawerOpen: () => void;
  onNoteChange: (v: string) => void;
  onDateClick: () => void;
  onTimeClick: () => void;
}

const BPForm: React.FC<BPFormProps> = ({
  systolic,
  diastolic,
  pulse,
  moreOpen,
  context,
  note,
  date,
  timeDate,
  onSystolicChange,
  onDiastolicChange,
  onPulseChange,
  onMoreToggle,
  onContextDrawerOpen,
  onNoteChange,
  onDateClick,
  onTimeClick,
}) => {
  const { t } = useTranslation();
  const contextLabel = context
    ? CONTEXT_OPTIONS.find((o) => o.value === context)?.label
    : null;

  return (
    <>
      {/* Blood pressure */}
      <div className={styles.fieldGroup}>
        <p className={styles.fieldLabel}>{t("vitalsTracker.bloodPressure")}</p>
        <div className={styles.bpRow}>
          <div className={styles.bpInputWrap}>
            <div className={styles.underlineRow}>
              <input
                className={styles.underlineInput}
                type="number"
                inputMode="numeric"
                placeholder="100"
                value={systolic}
                onChange={(e) => onSystolicChange(e.target.value)}
                min={1}
                max={300}
              />
            </div>
          </div>
          <span className={styles.bpSeparator}>/</span>
          <div className={styles.bpInputWrap}>
            <div className={styles.underlineRow}>
              <input
                className={styles.underlineInput}
                type="number"
                inputMode="numeric"
                placeholder="70"
                value={diastolic}
                onChange={(e) => onDiastolicChange(e.target.value)}
                min={1}
                max={200}
              />
            </div>
          </div>
          <div className={styles.bpUnitWrap}>
            <span className={styles.unitText}>mmHg</span>
          </div>
        </div>
      </div>

      {/* Pulse */}
      <div className={styles.fieldGroup}>
        <p className={styles.fieldLabel}>
          {t("vitalsTracker.addReading.pulse")}
        </p>
        <div className={styles.underlineRow}>
          <input
            className={styles.underlineInput}
            type="number"
            inputMode="numeric"
            placeholder={t("vitalsTracker.addReading.pulsePlaceholder")}
            value={pulse}
            onChange={(e) => onPulseChange(e.target.value)}
            min={1}
            max={300}
          />
          <span className={styles.unitText}>bpm</span>
        </div>
      </div>

      <WhenRow
        date={date}
        timeDate={timeDate}
        onDateClick={onDateClick}
        onTimeClick={onTimeClick}
      />

      {/* More details accordion */}
      <div>
        <button
          className={styles.accordionTrigger}
          onClick={onMoreToggle}
          type="button"
          aria-expanded={moreOpen}
        >
          <span className={styles.accordionLabel}>
            {t("vitalsTracker.addReading.moreDetails")}
          </span>
          <IconChevronDown
            size={18}
            className={`${styles.accordionChevron}${
              moreOpen ? ` ${styles.open}` : ""
            }`}
          />
        </button>
        {moreOpen && (
          <div className={styles.accordionBody}>
            <div className={styles.fieldGroup}>
              <p className={styles.fieldLabel}>
                {t("vitalsTracker.addReading.context")}
              </p>
              <button
                className={styles.contextTrigger}
                onClick={onContextDrawerOpen}
                type="button"
              >
                <span
                  className={`${styles.contextValue}${
                    contextLabel ? ` ${styles.selected}` : ""
                  }`}
                >
                  {contextLabel ??
                    t("vitalsTracker.addReading.contextPlaceholder")}
                </span>
                <IconChevronDown size={16} color="#7046ae" />
              </button>
            </div>
            <NoteSection note={note} onChange={onNoteChange} />
          </div>
        )}
      </div>
    </>
  );
};

// ── Glucose form ──────────────────────────────────────────────────────────────

interface GlucoseFormProps {
  value: string;
  category: GlucoseCategory | null;
  note: string;
  date: string;
  timeDate: Date;
  onValueChange: (v: string) => void;
  onCategoryDrawerOpen: () => void;
  onNoteChange: (v: string) => void;
  onDateClick: () => void;
  onTimeClick: () => void;
}

const GlucoseForm: React.FC<GlucoseFormProps> = ({
  value,
  category,
  note,
  date,
  timeDate,
  onValueChange,
  onCategoryDrawerOpen,
  onNoteChange,
  onDateClick,
  onTimeClick,
}) => {
  const { t } = useTranslation();
  const [noteOpen, setNoteOpen] = useState(false);
  return (
    <>
      {/* Blood sugar value */}
      <div className={styles.fieldGroup}>
        <p className={styles.fieldLabel}>{t("vitalsTracker.bloodSugar")}</p>
        <div className={styles.underlineRow}>
          <input
            className={styles.underlineInput}
            type="number"
            inputMode="decimal"
            placeholder={t("vitalsTracker.addReading.pulsePlaceholder")}
            value={value}
            onChange={(e) => onValueChange(e.target.value)}
            min={1}
            max={1000}
          />
          <span className={styles.unitText}>mg/dL</span>
        </div>
      </div>

      {/* Category */}
      <div className={styles.fieldGroup}>
        <p className={styles.fieldLabel}>
          {t("vitalsTracker.addReading.category")}
        </p>
        <button
          className={styles.contextTrigger}
          onClick={onCategoryDrawerOpen}
          type="button"
        >
          <span
            className={`${styles.contextValue}${
              category ? ` ${styles.selected}` : ""
            }`}
          >
            {category ?? t("vitalsTracker.addReading.contextPlaceholder")}
          </span>
          <IconChevronDown size={16} color="#7046ae" />
        </button>
      </div>

      <WhenRow
        date={date}
        timeDate={timeDate}
        onDateClick={onDateClick}
        onTimeClick={onTimeClick}
      />

      <div>
        <button
          className={styles.accordionTrigger}
          onClick={() => setNoteOpen((p) => !p)}
          type="button"
          aria-expanded={noteOpen}
        >
          <span className={styles.accordionLabel}>
            {t("vitalsTracker.addReading.note")}
          </span>
          <IconChevronDown
            size={18}
            className={`${styles.accordionChevron}${
              noteOpen ? ` ${styles.open}` : ""
            }`}
          />
        </button>
        {noteOpen && (
          <div className={styles.accordionBody}>
            <NoteSection
              note={note}
              onChange={onNoteChange}
              showLabel={false}
              countBelow
            />
          </div>
        )}
      </div>
    </>
  );
};

// ── Weight form ───────────────────────────────────────────────────────────────

interface WeightFormProps {
  value: string;
  unit: "kg" | "lb";
  note: string;
  date: string;
  timeDate: Date;
  onValueChange: (v: string) => void;
  onUnitChange: (u: "kg" | "lb") => void;
  onNoteChange: (v: string) => void;
  onDateClick: () => void;
  onTimeClick: () => void;
}

const WeightForm: React.FC<WeightFormProps> = ({
  value,
  unit,
  note,
  date,
  timeDate,
  onValueChange,
  onUnitChange,
  onNoteChange,
  onDateClick,
  onTimeClick,
}) => {
  const { t } = useTranslation();
  const [noteOpen, setNoteOpen] = useState(false);
  return (
    <>
      {/* Weight value + unit toggle */}
      <div className={styles.fieldGroup}>
        <p className={styles.fieldLabel}>{t("vitalsTracker.weight")}</p>
        <div className={styles.weightRow}>
          <div className={styles.underlineRow} style={{ flex: 1 }}>
            <input
              className={styles.underlineInput}
              type="number"
              inputMode="decimal"
              placeholder={t("vitalsTracker.addReading.pulsePlaceholder")}
              value={value}
              onChange={(e) => onValueChange(e.target.value)}
              min={1}
              max={500}
            />
            <span className={styles.unitText}>{unit}</span>
          </div>
          <div className={styles.unitToggle}>
            <button
              className={`${styles.unitPill}${
                unit === "kg" ? ` ${styles.unitPillActive}` : ""
              }`}
              onClick={() => onUnitChange("kg")}
              type="button"
            >
              kg
            </button>
            <button
              className={`${styles.unitPill}${
                unit === "lb" ? ` ${styles.unitPillActive}` : ""
              }`}
              onClick={() => onUnitChange("lb")}
              type="button"
            >
              lb
            </button>
          </div>
        </div>
      </div>

      <WhenRow
        date={date}
        timeDate={timeDate}
        onDateClick={onDateClick}
        onTimeClick={onTimeClick}
      />

      <div>
        <button
          className={styles.accordionTrigger}
          onClick={() => setNoteOpen((p) => !p)}
          type="button"
          aria-expanded={noteOpen}
        >
          <span className={styles.accordionLabel}>
            {t("vitalsTracker.addReading.note")}
          </span>
          <IconChevronDown
            size={18}
            className={`${styles.accordionChevron}${
              noteOpen ? ` ${styles.open}` : ""
            }`}
          />
        </button>
        {noteOpen && (
          <div className={styles.accordionBody}>
            <NoteSection
              note={note}
              onChange={onNoteChange}
              showLabel={false}
              countBelow
            />
          </div>
        )}
      </div>
    </>
  );
};

// ── Main modal ────────────────────────────────────────────────────────────────

export interface AddReadingModalProps {
  isOpen: boolean;
  onClose: () => void;
  parameter: VitalsParameter;
  memberId: string;
  onSuccess?: () => void;
  isFirstTime?: boolean;
  editReading?: VitalsReading;
}

const AddReadingModal: React.FC<AddReadingModalProps> = ({
  isOpen,
  onClose,
  parameter,
  memberId,
  onSuccess,
  isFirstTime = false,
  editReading,
}) => {
  const { t } = useTranslation();
  const isEditMode = editReading != null;

  // Shared
  const [date, setDate] = useState(() =>
    editReading
      ? format(new Date(editReading.recordedAt), "yyyy-MM-dd")
      : todayDateString()
  );
  const [timeDate, setTimeDate] = useState(() =>
    editReading ? new Date(editReading.recordedAt) : new Date()
  );
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // BP-specific
  const [systolic, setSystolic] = useState(() =>
    editReading?.parameter === VitalsParameter.BP
      ? String(editReading.values.systolic ?? "")
      : ""
  );
  const [diastolic, setDiastolic] = useState(() =>
    editReading?.parameter === VitalsParameter.BP
      ? String(editReading.values.diastolic ?? "")
      : ""
  );
  const [pulse, setPulse] = useState(() =>
    editReading?.parameter === VitalsParameter.BP
      ? String(editReading.values.pulse ?? "")
      : ""
  );
  const [moreOpen, setMoreOpen] = useState(
    () =>
      editReading?.parameter === VitalsParameter.BP &&
      editReading.context != null
  );
  const [context, setContext] = useState<ContextValue>(() =>
    editReading?.parameter === VitalsParameter.BP
      ? (editReading.context as ContextValue) ?? null
      : null
  );
  const [contextDrawerOpen, setContextDrawerOpen] = useState(false);

  // Glucose-specific
  const [glucoseValue, setGlucoseValue] = useState(() =>
    editReading?.parameter === VitalsParameter.GLUCOSE
      ? String(editReading.values.value ?? "")
      : ""
  );
  const [glucoseCategory, setGlucoseCategory] =
    useState<GlucoseCategory | null>(() =>
      editReading?.parameter === VitalsParameter.GLUCOSE
        ? (editReading.values.category as GlucoseCategory) ?? null
        : null
    );
  const [categoryDrawerOpen, setCategoryDrawerOpen] = useState(false);

  // Weight-specific
  const [weightValue, setWeightValue] = useState(() =>
    editReading?.parameter === VitalsParameter.WEIGHT
      ? String(editReading.values.value ?? "")
      : ""
  );
  const [weightUnit, setWeightUnit] = useState<"kg" | "lb">("kg");

  const resetForm = useCallback(() => {
    setDate(todayDateString());
    setTimeDate(new Date());
    setNote("");
    setError(null);
    setSystolic("");
    setDiastolic("");
    setPulse("");
    setMoreOpen(false);
    setContext(null);
    setGlucoseValue("");
    setGlucoseCategory(null);
    setWeightValue("");
    setWeightUnit("kg");
  }, []);

  const handleDateDone = useCallback((d: Date) => {
    setDate(format(d, "yyyy-MM-dd"));
    setDatePickerOpen(false);
  }, []);

  const handleTimeDone = useCallback(
    (d: Date) => {
      const combined = combineDateTime(date, d);
      const now = new Date();
      setTimeDate(combined > now ? now : d);
      setTimePickerOpen(false);
    },
    [date]
  );

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const canSave =
    parameter === VitalsParameter.BP
      ? systolic.trim() !== "" && diastolic.trim() !== ""
      : parameter === VitalsParameter.GLUCOSE
      ? glucoseValue.trim() !== ""
      : weightValue.trim() !== "";

  const handleSave = useCallback(async () => {
    if (!canSave || saving) return;
    setSaving(true);
    setError(null);
    try {
      const recordedAt = combineDateTime(date, timeDate);
      let dto: RecordReadingDto;

      if (parameter === VitalsParameter.BP) {
        dto = {
          parameter: VitalsParameter.BP,
          values: {
            systolic: Number(systolic),
            diastolic: Number(diastolic),
            ...(pulse ? { pulse: Number(pulse) } : {}),
          },
          recordedAt,
          ...(context ? { context } : {}),
          ...(note.trim() ? { note: note.trim() } : {}),
        };
      } else if (parameter === VitalsParameter.GLUCOSE) {
        dto = {
          parameter: VitalsParameter.GLUCOSE,
          values: {
            value: Number(glucoseValue),
            ...(glucoseCategory ? { category: glucoseCategory } : {}),
          },
          recordedAt,
          ...(note.trim() ? { note: note.trim() } : {}),
        };
      } else {
        const rawWeight = Number(weightValue);
        dto = {
          parameter: VitalsParameter.WEIGHT,
          values: {
            value: weightUnit === "lb" ? lbsToKg(rawWeight) : rawWeight,
          },
          recordedAt,
          ...(note.trim() ? { note: note.trim() } : {}),
        };
      }

      if (isEditMode && editReading) {
        const updateDto: UpdateReadingDto = {
          values: dto.values,
          recordedAt: dto.recordedAt,
          ...(dto.context ? { context: dto.context } : { context: undefined }),
          ...(dto.note ? { note: dto.note } : {}),
        };
        await vitalsTrackingService.updateReading(
          memberId,
          editReading.id,
          updateDto
        );
      } else if (isFirstTime) {
        await vitalsTrackingService.enableTracking(memberId, [parameter], {
          [parameter]: dto.values as Record<string, unknown>,
        });
      } else {
        await vitalsTrackingService.recordReading(memberId, dto);
      }

      resetForm();
      onSuccess?.();
      onClose();
    } catch {
      setError(t("vitalsTracker.addReading.saveError"));
    } finally {
      setSaving(false);
    }
  }, [
    canSave,
    saving,
    parameter,
    date,
    timeDate,
    systolic,
    diastolic,
    pulse,
    context,
    note,
    glucoseValue,
    glucoseCategory,
    weightValue,
    weightUnit,
    memberId,
    isFirstTime,
    isEditMode,
    editReading,
    resetForm,
    onClose,
    onSuccess,
    t,
  ]);

  return (
    <>
      <MDialog
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? t("vitalsTracker.readingsDetail.editReadingTitle")
            : t("vitalsTracker.addReading.title")
        }
        showPrimaryCta={false}
        disableOutsideClick
      >
        <div className={styles.formBody}>
          {parameter === VitalsParameter.BP && (
            <BPForm
              systolic={systolic}
              diastolic={diastolic}
              pulse={pulse}
              moreOpen={moreOpen}
              context={context}
              note={note}
              date={date}
              timeDate={timeDate}
              onSystolicChange={setSystolic}
              onDiastolicChange={setDiastolic}
              onPulseChange={setPulse}
              onMoreToggle={() => setMoreOpen((p) => !p)}
              onContextDrawerOpen={() => setContextDrawerOpen(true)}
              onNoteChange={setNote}
              onDateClick={() => setDatePickerOpen(true)}
              onTimeClick={() => setTimePickerOpen(true)}
            />
          )}

          {parameter === VitalsParameter.GLUCOSE && (
            <GlucoseForm
              value={glucoseValue}
              category={glucoseCategory}
              note={note}
              date={date}
              timeDate={timeDate}
              onValueChange={setGlucoseValue}
              onCategoryDrawerOpen={() => setCategoryDrawerOpen(true)}
              onNoteChange={setNote}
              onDateClick={() => setDatePickerOpen(true)}
              onTimeClick={() => setTimePickerOpen(true)}
            />
          )}

          {parameter === VitalsParameter.WEIGHT && (
            <WeightForm
              value={weightValue}
              unit={weightUnit}
              note={note}
              date={date}
              timeDate={timeDate}
              onValueChange={setWeightValue}
              onUnitChange={setWeightUnit}
              onNoteChange={setNote}
              onDateClick={() => setDatePickerOpen(true)}
              onTimeClick={() => setTimePickerOpen(true)}
            />
          )}

          {error && <p className={styles.errorText}>{error}</p>}

          <div className={styles.modalActions}>
            <MButton
              variant="outline"
              size="sm"
              onClick={handleClose}
              type="button"
            >
              {t("vitalsTracker.addReading.cancel")}
            </MButton>
            <MButton
              variant="filled"
              size="sm"
              onClick={handleSave}
              disabled={!canSave || saving}
              type="button"
            >
              {saving ? t("common.saving") : t("vitalsTracker.addReading.save")}
            </MButton>
          </div>
        </div>
      </MDialog>

      {/* All drawers rendered outside MDialog to avoid z-index stacking context issues */}
      <MDatePicker
        open={datePickerOpen}
        onClose={() => setDatePickerOpen(false)}
        onDone={handleDateDone}
        initialValue={(() => {
          const [y, m, d] = date.split("-").map(Number);
          return new Date(y, m - 1, d);
        })()}
        maxDate={new Date()}
        title="Select date"
      />

      <MTimePicker
        open={timePickerOpen}
        onClose={() => setTimePickerOpen(false)}
        onDone={handleTimeDone}
        initialValue={timeDate}
        format={12}
      />

      <MDropDownDrawer
        open={contextDrawerOpen}
        onClose={() => setContextDrawerOpen(false)}
        title="Context"
        items={CONTEXT_OPTIONS.map(
          (o): DropdownItem => ({ value: o.value, label: o.label })
        )}
        initialValue={context ?? ""}
        onSelect={(v) => setContext(v as ContextValue)}
      />

      <MDropDownDrawer
        open={categoryDrawerOpen}
        onClose={() => setCategoryDrawerOpen(false)}
        title="Category"
        items={GLUCOSE_CATEGORIES.map(
          (c): DropdownItem => ({ value: c, label: c })
        )}
        initialValue={glucoseCategory ?? ""}
        onSelect={(v) => setGlucoseCategory(v as GlucoseCategory)}
      />
    </>
  );
};

export default AddReadingModal;
